import { useQueryClient } from 'react-query'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useCreateExternalIntegration } from 'screens/OAuthRedirectV2/hooks'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { createIntegrationAuths } from 'components/Onboarding/CreateBrandStyleModal/CreateBrandStyleModal'
import { fetchConnectableAccounts } from 'hooks/queries/workspaces/integrationAuths/useConnectableAccounts'
import { showIntegrationSuccessToast } from 'common/ToastMessage/ShowToastMessage'
import { showIntegrationErrorToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'

export const useOauthRefresh = () => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const { mutateAsync: createExternalIntegration } = useCreateExternalIntegration()
  const { data: externalIntegrationsData } = useExternalIntegrations()

  const handleRefresh = async (
    params: any,
    provider: ExternalIntegrationEnum,
    accountToRefreshId: number,
    onSuccess?: (integration: any) => void,
  ) => {
    try {
      const { data: integrationAuth } = await createIntegrationAuths({
        provider,
        params,
        workspaceId,
      })

      const { data: connectableAccounts } = await queryClient.fetchQuery(
        ['workspaces', workspaceId, 'integrationAuths', integrationAuth.id, 'connectableAccounts'],
        () => fetchConnectableAccounts(integrationAuth.id, workspaceId),
      )

      if (connectableAccounts.length === 0) {
        showIntegrationErrorToast('No accounts found to refresh')
        return
      }

      const existingIntegration = externalIntegrationsData?.external_integrations.find(
        (integration) => integration.id === Number(accountToRefreshId),
      )

      const accountToRefresh = connectableAccounts.find(
        (account) => account.provider_id === existingIntegration?.provider_id,
      )

      if (!accountToRefresh) {
        showIntegrationErrorToast('Account to refresh not found')
        return
      }

      const result = await createExternalIntegration({
        workspaceId,
        params: {
          ...accountToRefresh,
          integration_auth_id: integrationAuth.id,
          account_to_refresh_id: accountToRefreshId,
        },
      })

      if (result.type === 'success') {
        showIntegrationSuccessToast(`Successfully refreshed ${accountToRefresh.name}!`)
        queryClient.invalidateQueries(['externalIntegrations', workspaceId])
        onSuccess?.(result.data.external_integration)
      } else {
        showIntegrationErrorToast('Failed to refresh account')
      }
    } catch (error) {
      showIntegrationErrorToast('Failed to refresh account')
    }
  }

  return { handleRefresh }
}
